import React, {Fragment, useEffect, useState} from 'react';
import Dialog from '@material-ui/core/Dialog';
import {withStyles} from '@material-ui/core/styles';
import {useTranslation} from "react-i18next";
import MuiDialogTitle from "@material-ui/core/DialogTitle/DialogTitle";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from '@material-ui/icons/Close';
import MuiDialogContent from "@material-ui/core/DialogContent/DialogContent";
import {useForm} from "react-hook-form";
import {Grid, TextField, Snackbar} from "@material-ui/core";
import ButtonAtm from "../../../../atomic/Button/Button";
import ColAtm from "../../../../atomic/Col/Col";
import { ValidatorForm} from 'react-material-ui-form-validator';
import AppBar from "@material-ui/core/AppBar/AppBar";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Box from "@material-ui/core/Box";
import PropTypes from "prop-types";
import {
    getFactList,
    updateFactClearState,
    updateFactMapping
} from "../../../../../redux/actions/Content/FactList/factListActions";
import {useDispatch, useSelector} from "react-redux";
import {isDefined, getUserToken, getAppLang, getProperty} from "../../../../../utils";
import {Alert} from "@material-ui/lab";
import axios from 'axios';
import { OPEN_AI } from '../../../../../constants/api.constants';
import { getLanguages } from '../../../../../redux/actions/languageActions';

const styles = theme => ({
    root: {
        margin: 0,
        padding: theme.spacing(2),
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
});

const DialogTitle = withStyles(styles)(props => {
    const { children, classes, onClose, ...other } = props;
    return (
        <MuiDialogTitle disableTypography className={classes.root} {...other}>
            <Typography variant="h6">{children}</Typography>
            {onClose ? (
                <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
                    <CloseIcon className="close-button" />
                </IconButton>
            ) : null}
        </MuiDialogTitle>
    );
});

const DialogContent = withStyles(theme => ({
    root: {
        padding: theme.spacing(2),
    },
}))(MuiDialogContent);

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <Typography
            component="div"
            role="tabpanel"
            hidden={value !== index}
            id={`room-rate-tabpanel-${index}`}
            aria-labelledby={`room-rate-tab-${index}`}
            {...other}
        >
            {value === index && <Box>{children}</Box>}
        </Typography>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.any.isRequired,
    value: PropTypes.any.isRequired,
};

function a11yProps(index) {
    return {
        id: `room-rate-tab-${index}`,
        'aria-controls': `room-rate-tabpanel-${index}`,
    };
}

export default function FactDescriptionModal({description, id, pageId, type}) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { handleSubmit } = useForm();

    const factData = useSelector(props => props.updateFactMappingReducer);
    const languageList = useSelector(state => state.languagesReducer.languages);

    const [open, setOpen] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const [state, setState] = useState({
        description: description
    });
    const [translateLoading, setTranslateLoading] = useState(false);
    const [showSnackbar, setShowSnackbar] = useState(false);

    useEffect(() => {
        if (!languageList || !languageList.length) {
            dispatch(getLanguages());
        }
    }, []);

    const handleClickOpen = () => {
        setOpen(true);
        dispatch(updateFactClearState());
    };
    const handleClose = () => {
        setOpen(false);
    };

    const handleTabChange = ( event, newValue ) => {
        setTabValue(newValue)
    };

    const handleChangeDescription = index => (e) => {
        let values = state.description;
        values[index]['description'] = e.target.value;
        setState({ ...state, values });
    };

    const onSubmit = () => {
        let params;

        params = {
            property_fact: [{
                id: id,
                is_selected: true,
                description: state.description,
            }]
        }

        dispatch(updateFactMapping(params))
    };

    const handleTranslate = (langCode) => async (e) => {
        const currentDesc = state.description.find(d => d.language_code === langCode);

        if (!currentDesc?.description || currentDesc.description.trim() === "") {
            return;
        }

        setTranslateLoading(true);

        const sourceLangName = (languageList || []).find(lang => lang?.code === langCode)?.name || langCode;

        const targetLangs = (languageList || [])
            .map(lang => ({ code: lang?.code, name: lang?.name }))
            .filter(lang => lang.code);

        const headers = {
            'Content-Type': 'application/json',
            'authToken': getUserToken(),
            'language': getAppLang(),
        };

        const makeRequest = (targetLang) => {
            const body = {
                params: {
                    property_id: getProperty(),
                    query: {
                        model: "gpt-4.1-mini",
                        response_format: { type: "json_object" },
                        messages: [
                            {
                                role: "system",
                                content: "Sen profesyonel bir çevirmensin. Cevabını SADECE JSON formatında ver, tek bir key olsun: dil kodu (örn: {\"en\": \"...\"})."
                            },
                            {
                                role: "user",
                                content: `Aşağıdaki ${sourceLangName} dilindeki metni ${targetLang.name} diline çevir. Metin: "${currentDesc.description}". Çeviriyi JSON formatında ver, key olarak "${targetLang.code}" kullan.`
                            }
                        ]
                    }
                }
            };
            return axios.post(OPEN_AI, body, { headers })
                .then(res => res.data.data)
                .catch(() => null);
        };

        try {
            const results = await Promise.all(targetLangs.map(lang => makeRequest(lang)));

            const merged = {};
            results.forEach(result => {
                if (result && typeof result === 'object') {
                    Object.assign(merged, result);
                }
            });

            handleTranslateResponse(merged, langCode);
        } finally {
            setTranslateLoading(false);
        }
    };

    const handleTranslateResponse = (aiData, sourceLangCode) => {
        let updatedDescriptions = state.description.map(desc => ({ ...desc }));

        Object.keys(aiData || {}).forEach(lang => {
            if (lang !== sourceLangCode) {
                const idx = updatedDescriptions.findIndex(d => d.language_code === lang);
                if (idx !== -1 && aiData[lang]) {
                    updatedDescriptions[idx].description = aiData[lang];
                }
            }
        });

        setState(prev => ({
            ...prev,
            description: updatedDescriptions
        }));

        setShowSnackbar(true);
    };

    useEffect( () => {
        if (factData.updateFactMappingStatus === 200 && open) {
            setOpen(false);
            dispatch(getFactList(pageId));
        }
    },[factData])

    const sortedDescriptions = state.description
        .map((item, idx) => ({ ...item, originalIndex: idx }))
        .sort((a, b) => {
            if (a.language_code === "tr") return -1;
            if (b.language_code === "tr") return 1;
            if (a.language_code === "en") return -1;
            if (b.language_code === "en") return 1;
            return a.language_code.localeCompare(b.language_code);
        });

    return (
        <>
            <ButtonAtm className="edit-channel-price-btn"
                       endIcon={<i className="fas fa-edit" />}
                       onClick={handleClickOpen}
                       title={t('btn-update')}
            />
            <Dialog
                open={open}
                onClose={handleClose}
                disableBackdropClick={true}
                aria-labelledby="responsive-dialog-title"
                key={3}
            >
                <DialogTitle id="customized-dialog-title" className="red-bottom-border" onClose={handleClose}>
                    { t('enw-fact_description_title') }
                </DialogTitle>
                <DialogContent>
                    <Fragment>
                        <style>{`
                            button.Mui-disabled {
                                color:#000 !important;
                            }
                            .fact-text-field {
                                margin-top:0px!important;
                            }
                        `}</style>
                        <ValidatorForm onSubmit={handleSubmit(onSubmit)}>
                            {isDefined(factData) && factData.updateFactMappingStatus === 200 && <Alert severity="success">{ t('enw-redirect_to_save-message') }</Alert>}
                            {isDefined(factData.error.response) && <Alert severity="error">{factData.error.response.data.message}</Alert>}

                            <ColAtm className="d-block mb-55 fact-desc-container">
                                <ColAtm>
                                    <AppBar className="room-rate-tab-title" position="static">
                                        <Tabs className="room-rate-tabFont"
                                              value={tabValue}
                                              onChange={handleTabChange}
                                              aria-label="room-rate-tab"
                                              variant="scrollable"
                                              scrollButtons="on"
                                        >
                                            {
                                                sortedDescriptions.map((title, value) => {
                                                    const flag = (
                                                        <Grid container className="brand-desc-flag" spacing={1}>
                                                            <Grid item sm={3}>
                                                                <img src={`${process.env.PUBLIC_URL}/assets/img/language/${title.language_code}.svg`} alt={title.language_code} />
                                                            </Grid>
                                                            <Grid item sm={2}>{title.language_code}</Grid>
                                                        </Grid>
                                                    );
                                                    return (
                                                        <Tab label={flag} {...a11yProps(value)} key={title.language_code} />
                                                    );
                                                })
                                            }
                                        </Tabs>
                                    </AppBar>

                                    {
                                        sortedDescriptions.map((title, index) => {
                                            const hasDescription = title?.description && title.description.trim() !== "";
                                            return (
                                                <TabPanel value={tabValue} index={index} className="w-100" key={title.language_code}>
                                                    <Box display="flex" justifyContent="flex-end" mb={1}>
                                                        <ButtonAtm
                                                            size="small"
                                                            variant="outlined"
                                                            type="button"
                                                            onClick={handleTranslate(title.language_code)}
                                                            startIcon={
                                                                translateLoading ?
                                                                    <i style={{fontSize: "16px", marginLeft: "8px"}} className="fas fa-spinner-third fa-spin"></i>
                                                                    :
                                                                    <svg fill="#d71f26" style={{width: "1.5rem", height: "1rem", fontSize: 20, marginLeft: "8px"}} viewBox="0 0 52 52" xmlns="http://www.w3.org/2000/svg">
                                                                        <path d="M39,18.67H35.42l-4.2,11.12A29,29,0,0,1,20.6,24.91a28.76,28.76,0,0,0,7.11-14.49h5.21a2,2,0,0,0,0-4H19.67V2a2,2,0,1,0-4,0V6.42H2.41a2,2,0,0,0,0,4H7.63a28.73,28.73,0,0,0,7.1,14.49A29.51,29.51,0,0,1,3.27,30a2,2,0,0,0,.43,4,1.61,1.61,0,0,0,.44-.05,32.56,32.56,0,0,0,13.53-6.25,32,32,0,0,0,12.13,5.9L22.83,52H28l2.7-7.76H43.64L46.37,52h5.22Zm-15.3-8.25a23.76,23.76,0,0,1-6,11.86,23.71,23.71,0,0,1-6-11.86Zm8.68,29.15,4.83-13.83L42,39.57Z"/>
                                                                    </svg>
                                                            }
                                                            className="mt-12"
                                                            style={{borderRadius: "8px", color: "#000", textTransform: "none"}}
                                                            buttontext={t('btn-translate')}
                                                            disabled={!hasDescription || translateLoading}
                                                        />
                                                    </Box>
                                                    <TextField name={title.language_code}
                                                               type="text"
                                                               variant="outlined"
                                                               className="text-capitalize fact-text-field"
                                                               value={state.description[title.originalIndex]?.description || ""}
                                                               onChange={handleChangeDescription(title.originalIndex)}
                                                               label={ t('enw-fact_description') + " - " + title.language_code }
                                                               multiline={true}
                                                               fullWidth
                                                               required={false}
                                                               rows={2}
                                                    />
                                                </TabPanel>
                                            );
                                        })
                                    }
                                </ColAtm>
                            </ColAtm>

                            <ColAtm className="mt-13" md={12}>
                                <ButtonAtm
                                    className="m-auto room-rate-modal-save bg-red"
                                    type="submit"
                                    buttontext={ t('btn-save') }
                                    color="primary"
                                />
                            </ColAtm>
                        </ValidatorForm>
                    </Fragment>
                    <Snackbar
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        open={showSnackbar}
                        autoHideDuration={3000}
                        onClose={() => setShowSnackbar(false)}
                    >
                        <Alert onClose={() => setShowSnackbar(false)} severity="success">
                            {t('enw-translated_to_all_languages')}
                        </Alert>
                    </Snackbar>
                </DialogContent>
            </Dialog>
        </>
    );
}